// UI-kit galéria — a globális komponensek + a JELENLEGI design-rendszer élő, hiteles referenciája.
// FONTOS: ez CSAK showcase — a tényleges primitíveket (Text/Badge/Button/DataTable/tokenek) NEM definiálja,
// csak tükrözi. Az itt látott értékek a forrásból származnak: text.tsx (Text-variánsok), globals.css (szín-
// tokenek), badge.tsx, button.tsx, data-table.tsx.
//
// NINCS MENÜPONTJA (user-döntés): fejlesztői eszköz, nem termék-felület, ezért a navigációból
// kivezettük (a „Fejlesztői" nav-csoport ezzel megszűnt). A lap URL-ről továbbra is elérhető (`/ui-kit`) —
// ez szándékos: a design-rendszer élő referenciájára a fejlesztésnél szükség van, adatot nem olvas, és
// jog-kaput sem igényel (a route csak bejelentkezést kér, mint minden `_authenticated` lap).
import {
  BoltIcon,
  CheckCircleIcon,
  ClockIcon,
  IdentificationIcon,
  PencilSquareIcon,
  ReceiptPercentIcon,
  Squares2X2Icon,
} from '@heroicons/react/24/outline';
import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';

import { AccountCategoryBadge } from '@/components/accounts/account-category-badge';
import { AccountStatusBadge } from '@/components/accounts/account-status-badge';
import { AtlasMark, AtlasWordmark } from '@/components/brand/atlas-logo';
import i18n from '@/i18n';
import { AppShell } from '@/components/shared/app-shell';
import { ConfirmDialog, type ConfirmTone } from '@/components/shared/confirm-dialog';
import { DataTable, type DataTableColumn } from '@/components/shared/data-table';
import { EmptyState } from '@/components/shared/empty-state';
import { PageBody } from '@/components/shared/page-body';
import { Avatar } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { DatePicker } from '@/components/ui/date-picker';
import {
  Dialog,
  DialogBody,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Divider } from '@/components/ui/divider';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { PasswordInput } from '@/components/ui/password-input';
import { Segmented } from '@/components/ui/segmented';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Text } from '@/components/ui/text';
import { Textarea } from '@/components/ui/textarea';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { requireAuth } from '@/lib/auth';
import { cn } from '@/lib/utils';

export const Route = createFileRoute('/ui-kit')({
  beforeLoad: () => requireAuth(),
  component: UiKitPage,
});

function Section({ title, children }: { title: string; children: React.ReactNode }): JSX.Element {
  return (
    <Card>
      <CardHeader>
        <CardTitle>{title}</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-wrap items-center gap-3">{children}</CardContent>
    </Card>
  );
}

/** Élő DatePicker-minta: a Popover-triggeres választó (bal) + a beágyazott Calendar (jobb). */
/**
 * Szegmentált választó — a `Segmented` primitív. MIKOR EZ a `PillField` HELYETT: ha a választásnak nincs saját
 * JELENTÉS-SZÍNE (típus / felület / címzés-fajta). A telített, tónusos pill STÁTUSZ-választásokhoz való.
 */
function SegmentedDemo(): JSX.Element {
  const [value, setValue] = useState('internal');
  return (
    <Segmented
      value={value}
      onChange={setValue}
      options={[
        { value: 'internal', label: 'Belső' },
        { value: 'external', label: 'Külső' },
      ]}
      aria-label="Példa — felület"
    />
  );
}

function DatePickerDemo(): JSX.Element {
  const [value, setValue] = useState('');
  return (
    <div className="flex flex-wrap items-start gap-6">
      <div className="w-48 space-y-1.5">
        <Text variant="secondary" as="label" className="block">
          Trigger (Popover)
        </Text>
        <DatePicker value={value} onChange={setValue} label="Válassz dátumot" language="hu" />
        <Text variant="secondary" as="p" className="text-subtle">
          érték: {value || '—'}
        </Text>
      </div>
      <div className="border-border rounded-lg border p-3">
        <Calendar
          selected={value ? new Date(value) : null}
          language="hu"
          onSelect={(d) => setValue(d.toISOString().slice(0, 10))}
        />
      </div>
    </div>
  );
}

/** Egy tipográfia-sor: bal oldalt az ÉLŐ `<Text>` minta, jobbra a spec (méret / súly / szín-szerep). */
function TypeRow({ spec, children }: { spec: string; children: React.ReactNode }): JSX.Element {
  return (
    <div className="border-border/60 flex w-full flex-wrap items-baseline justify-between gap-x-4 gap-y-1 border-b py-2.5 last:border-0">
      <div className="min-w-0">{children}</div>
      <span className="text-subtle shrink-0 font-mono text-xs">{spec}</span>
    </div>
  );
}

/** Szöveg-szín swatch: tömör szín-chip + ÉLŐ szöveg-minta a szerep VALÓS token-osztályával + light/dark hex. */
function Swatch({
  fill,
  text,
  role,
  cls,
  light,
  dark,
  note,
}: {
  fill: string;
  text: string;
  role: string;
  cls: string;
  light: string;
  dark: string;
  note: string;
}): JSX.Element {
  return (
    <div className="border-border bg-card/50 flex min-w-[13rem] flex-1 flex-col gap-2 rounded-lg border p-3">
      <div className="flex items-center gap-2.5">
        <span className={cn('border-border size-8 shrink-0 rounded-md border', fill)} />
        <div className="min-w-0">
          <Text variant="itemTitle" as="div" className="truncate">
            {role}
          </Text>
          <span className="text-subtle block truncate font-mono text-xs">{cls}</span>
        </div>
      </div>
      <Text variant="body" as="p" className={cn('leading-snug', text)}>
        Aa — a gyors, barna róka.
      </Text>
      <div className="flex flex-wrap gap-x-3 gap-y-0.5">
        <span className="text-subtle font-mono text-xs">L {light}</span>
        <span className="text-subtle font-mono text-xs">D {dark}</span>
      </div>
      <Text variant="secondary" as="div" className="text-subtle">
        {note}
      </Text>
    </div>
  );
}

interface DemoAccount {
  id: string;
  name: string;
  website: string;
  site: string;
  category: string;
  type: string;
  status: string;
  city: string;
}

const DEMO_ROWS: DemoAccount[] = [
  {
    id: '1',
    name: 'MORGENS Design Kft.',
    website: 'morgens.hu',
    site: 'roomlytics.io',
    category: 'A++',
    type: 'Hospitality',
    status: 'active',
    city: '1051 Budapest, Sas u. 10.',
  },
  {
    id: '2',
    name: 'Roomlytics Zrt.',
    website: 'roomlytics.io',
    site: 'app.roomlytics.io',
    category: 'A',
    type: 'Szálláshely',
    status: 'pending',
    city: '9021 Győr, Fő tér 2.',
  },
  {
    id: '3',
    name: 'Balaton Spa Kft.',
    website: 'balatonspa.hu',
    site: 'balatonspa.hu/foglalas',
    category: 'B',
    type: 'Spa',
    status: 'inactive',
    city: '8600 Siófok, Petőfi sétány 3.',
  },
];

const DEMO_COLUMNS: DataTableColumn<DemoAccount>[] = [
  {
    // Azonosító-oszlop: itemTitle anchor + SZOROS al-sor (URL, ~2px) + badge-csoport (nagyobb köz).
    id: 'name',
    header: 'Fiók',
    sortable: true,
    width: 'minmax(0,2.5fr)',
    multiline: true,
    cell: (r) => (
      <div className="min-w-0 space-y-1.5">
        <div className="min-w-0">
          <Text variant="itemTitle" as="span" className="block truncate">
            {r.name}
          </Text>
          <a
            href={`https://${r.website}`}
            target="_blank"
            rel="noreferrer"
            onClick={(e) => e.stopPropagation()}
            className="text-brand-hover mt-0.5 block truncate text-xs hover:underline"
          >
            {r.website}
          </a>
        </div>
        <div className="flex flex-wrap items-center gap-1">
          <AccountCategoryBadge category={r.category} />
          <Badge variant="default">{r.type}</Badge>
          {/* a státusz a VALÓDI fordításon megy (mint a fiók-listán) — a mintaoldal sem írhat nyers kulcsot */}
          <AccountStatusBadge
            status={r.status}
            label={i18n.t(`accounts.statusLabel.${r.status}`, { defaultValue: r.status })}
          />
        </div>
      </div>
    ),
  },
  {
    // Adat-cella: a DataTable alap-stílusa (13 / muted-foreground) — nincs külön szín-osztály.
    id: 'address',
    header: 'Cím',
    sortable: true,
    width: 'minmax(0,1.5fr)',
    cell: (r) => r.city,
  },
  {
    // Cellán belüli link: tompított zöld (brand-hover), hoveren aláhúzás.
    id: 'site',
    header: 'Weboldal',
    width: 'minmax(0,1.5fr)',
    cell: (r) => (
      <a
        href={`https://${r.site}`}
        target="_blank"
        rel="noreferrer"
        onClick={(e) => e.stopPropagation()}
        className="text-brand-hover block truncate hover:underline"
      >
        {r.site}
      </a>
    ),
  },
];

function UiKitPage(): JSX.Element {
  const [checked, setChecked] = useState(true);
  const [confirmTone, setConfirmTone] = useState<ConfirmTone | null>(null);

  return (
    <AppShell>
      <PageBody>
        <div className="grid gap-5">
          <Section title="Gombok — variánsok">
            <Button variant="cta">CTA (zöld gradient — csak a lista-belépő)</Button>
            <Button>Brand (MORGENS-zöld — az ALAPÉRTELMEZÉS)</Button>
            <Button variant="outline">Outline (alap)</Button>
            <Button variant="secondary">Secondary</Button>
            <Button variant="ghost">Ghost</Button>
            <Button variant="destructive">Destructive</Button>
            <Button variant="link">Link</Button>
            <Button disabled>Disabled</Button>
          </Section>

          <Section title="Gombok — méret-skála (22 / 28 / 32 / 38 · ikon 30)">
            <Button size="xs" variant="outline">
              XS — 22px
            </Button>
            <Button size="sm" variant="outline">
              SM — 28px
            </Button>
            <Button variant="outline">Default — 32px</Button>
            <Button size="lg" variant="outline">
              LG — 38px
            </Button>
            <Button size="icon" variant="outline" aria-label="Szerkesztés">
              <PencilSquareIcon />
            </Button>
          </Section>

          <Section title="Badge-ek — soft tónusok (szín = variant)">
            <Badge>Default</Badge>
            <Badge variant="success">Siker</Badge>
            <Badge variant="warning">Figyelem</Badge>
            <Badge variant="danger">Hiba</Badge>
            <Badge variant="info">Infó</Badge>
            <Badge variant="violet">Kategória</Badge>
            <Badge variant="muted">Piszkozat</Badge>
            <Badge variant="brand">MORGENS</Badge>
            <Badge variant="outline">Outline</Badge>
          </Section>

          <Section title="Badge-ek — opcionális vezető jel: pötty (dot) vagy ikon">
            <Badge variant="success" dot>
              Fizetve
            </Badge>
            <Badge variant="warning" dot>
              Kiállítva
            </Badge>
            <Badge variant="danger" dot>
              Lejárt
            </Badge>
            <Badge variant="success" icon={<CheckCircleIcon />}>
              Jóváhagyva
            </Badge>
            <Badge variant="warning" icon={<ClockIcon />}>
              Függőben
            </Badge>
            <Badge variant="brand" icon={<BoltIcon />}>
              Aktív
            </Badge>
            <Badge variant="danger" icon={<span className="size-1.5 rounded-sm bg-current" />}>
              Inaktív (négyzet-marker)
            </Badge>
          </Section>

          <Section title="Szegmentált választó (Segmented) — SEMLEGES dimenzió (típus, felület, címzés-fajta)">
            <SegmentedDemo />
          </Section>

          <Section title="Elválasztó (Divider) — a rendszer EGYETLEN vonala: behúzott alap, token-szín, 4 térköz">
            <div className="w-full max-w-md space-y-1">
              <Text variant="secondary" as="div">
                alap (behúzott, `md` térköz) — modálban/popoverben ez a helyes
              </Text>
              <div className="bg-card rounded-lg border">
                <div className="px-3 py-2 text-sm">tartalom</div>
                <Divider />
                <div className="px-3 py-2 text-sm">tartalom</div>
              </div>
              <Text variant="secondary" as="div" className="pt-2">
                `inset={false}` — teljes szélesség (tábla-sor, összegző vonal)
              </Text>
              <div className="bg-card rounded-lg border">
                <div className="px-3 py-2 text-sm">tartalom</div>
                <Divider inset={false} spacing="none" />
                <div className="px-3 py-2 text-sm">tartalom</div>
              </div>
              <Text variant="secondary" as="div" className="pt-2">
                {'`tone="hairline"` — a táblázatok halványabb vonala'}
              </Text>
              <div className="bg-card rounded-lg border">
                <div className="px-3 py-2 text-sm">tartalom</div>
                <Divider tone="hairline" inset={false} spacing="none" />
                <div className="px-3 py-2 text-sm">tartalom</div>
              </div>
            </div>
          </Section>

          <Section title="Dátumválasztó — globális DatePicker (Popover + hónap-rács Calendar; YYYY-MM-DD)">
            <DatePickerDemo />
          </Section>

          <Section title="Tipográfia — <Text> variánsok (skála 11 · 12 · 13 · 16 · súlyok 400/500/600 · Outfit)">
            <div className="w-full">
              <TypeRow spec="title · 16 / 600 / primary">
                <Text variant="title">Ügyfél részletei</Text>
              </TypeRow>
              <TypeRow spec="subtitle · 13 / 400 / secondary">
                <Text variant="subtitle">Alapadatok, számlázás és kapcsolattartók</Text>
              </TypeRow>
              <TypeRow spec="itemTitle · 13 / 500 / primary — lista-anchor">
                <Text variant="itemTitle">MORGENS Design Kft.</Text>
              </TypeRow>
              <TypeRow spec="body · 13 / 400 / primary">
                <Text variant="body">
                  Ez az alap, jól olvasható törzsszöveg — cellák és leírások.
                </Text>
              </TypeRow>
              <TypeRow spec="secondary · 12 / 400 / secondary">
                <Text variant="secondary">Frissítve 2 órája · created_by svc-odin@morgens.hu</Text>
              </TypeRow>
              <TypeRow spec="overline · 11 / 600 / secondary · CAPS + tracking">
                <Text variant="overline">Számlázási adatok</Text>
              </TypeRow>
            </div>
          </Section>

          <Section title="Szöveg-szín rámpa — szemantikus szerepek (téma-tudatos: világos + sötét)">
            <div className="flex w-full flex-wrap gap-3">
              <Swatch
                fill="bg-foreground"
                text="text-foreground"
                role="Primary"
                cls="text-foreground"
                light="#18211d"
                dark="#e9eeeb"
                note="Cím + törzsszöveg. AA ✓"
              />
              <Swatch
                fill="bg-muted-foreground"
                text="text-muted-foreground"
                role="Secondary"
                cls="text-muted-foreground"
                light="#4b5a52"
                dark="#b8ccc4"
                note="Label / fejléc / meta / supporting. AA ✓"
              />
              <Swatch
                fill="bg-subtle"
                text="text-subtle"
                role="Tertiary"
                cls="text-subtle"
                light="#697870"
                dark="#889792"
                note="CSAK placeholder / disabled / dekoratív. AA-határon (~4.6:1)."
              />
              <Swatch
                fill="bg-brand"
                text="text-brand"
                role="Brand"
                cls="text-brand"
                light="#38bd6d"
                dark="#38bd6d"
                note="Aktív / kiemelés. Téma-független."
              />
              <Swatch
                fill="bg-brand-hover"
                text="text-brand-hover"
                role="Link (tompított zöld)"
                cls="text-brand-hover"
                light="#229a5a"
                dark="#229a5a"
                note="Cellán belüli link; hoveren aláhúzás."
              />
            </div>
            <Divider spacing="none" inset={false} className="mt-1" />
            <div className="pt-3">
              <Text variant="overline" as="div" className="mb-1">
                Szín-politika
              </Text>
              <Text variant="secondary" as="p" className="leading-relaxed">
                primary = cím + body · secondary = label / fejléc / meta · tertiary CSAK placeholder
                / disabled / dekoratív (sose „muszáj-elolvasni” szöveg) · brand = aktív / kiemelés ·
                link = tompított zöld, hoveren aláhúzás.
              </Text>
            </div>
          </Section>

          <Section title="Lista / tábla-konvenciók — az accounts-minta (élő DataTable)">
            <div className="w-full">
              <Text variant="secondary" as="p" className="mb-3 leading-relaxed">
                Azonosító-oszlop = <code className="font-mono">itemTitle</code> anchor + SZOROS
                al-sor (~2px, pl. URL) + badge-csoport nagyobb köz · adat-cellák = 13 / muted (a
                DataTable alap) · oszlop-fejléc = overline (11 / 600 / muted, CAPS, truncate) ·
                cellán belüli link = tompított zöld, hoveren aláhúzás. Badge-sorrend: kategória-pill
                (kompakt „minőség”) → típus (semleges default) → státusz (szemantikus + ikon).
              </Text>
              <div className="flex h-[320px] flex-col">
                <DataTable
                  columns={DEMO_COLUMNS}
                  rows={DEMO_ROWS}
                  getRowId={(r) => r.id}
                  sort="name"
                  order="asc"
                  onSort={() => undefined}
                />
              </div>
            </div>
          </Section>

          <Section title="Avatar — monogram-fallback (determinisztikus paletta) + méretek">
            <Avatar name="Tóth Réka" size="sm" />
            <Avatar name="Tóth Réka" />
            <Avatar name="Tóth Réka" size="lg" />
            <Avatar name="Tóth Réka" size="xl" />
            <span className="bg-border mx-2 h-6 w-px" />
            <Avatar name="Ágnes Miklósi-Farkas" />
            <Avatar name="Bence Boós" />
            <Avatar name="Krisztián Biró" />
            <Avatar name="Enikő Varga" />
            <Avatar name="Zsolt Doma" />
          </Section>

          <Section title="Brand — AtlasMark + wordmark">
            <AtlasMark size={20} />
            <AtlasMark size={26} />
            <AtlasMark size={32} />
            <AtlasMark size={48} />
            <span className="bg-border mx-2 h-6 w-px" />
            <AtlasMark size={26} flat />
            <AtlasMark size={32} flat />
            <span className="bg-border mx-2 h-6 w-px" />
            <div className="flex items-center gap-2.5">
              <AtlasMark size={26} />
              <AtlasWordmark />
            </div>
          </Section>

          <Section title="Űrlap-elemek">
            <div className="grid w-full max-w-md gap-4">
              <div className="space-y-1.5">
                <Label htmlFor="demo-name">Név</Label>
                <Input id="demo-name" placeholder="pl. MORGENS Design Kft." />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="demo-pw">Jelszó (szemecske-toggle)</Label>
                <PasswordInput id="demo-pw" placeholder="••••••••" />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="demo-note">Megjegyzés</Label>
                <Textarea id="demo-note" placeholder="Több soros szöveg…" />
              </div>
              <div className="space-y-1.5">
                <Label>Account típus (csoportosított select)</Label>
                <Select>
                  <SelectTrigger>
                    <SelectValue placeholder="Válassz…" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectGroup>
                      <SelectLabel>Turizmus</SelectLabel>
                      <SelectItem value="hospitality">Hospitality</SelectItem>
                      <SelectItem value="accommodation">Szálláshely</SelectItem>
                      <SelectItem value="spa">Spa</SelectItem>
                    </SelectGroup>
                    <SelectGroup>
                      <SelectLabel>Egyéb</SelectLabel>
                      <SelectItem value="other_tourism">Egyéb turizmus</SelectItem>
                      <SelectItem value="other_industry">Egyéb iparág</SelectItem>
                    </SelectGroup>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center gap-2">
                <Checkbox
                  id="demo-cb"
                  checked={checked}
                  onCheckedChange={(v) => setChecked(v === true)}
                />
                <Label htmlFor="demo-cb">Aktív ügyfél</Label>
              </div>
              <div className="flex items-center gap-2">
                <Switch id="demo-sw" defaultChecked />
                <Label htmlFor="demo-sw">Automatikus számlázás</Label>
              </div>
            </div>
          </Section>

          <Section title="Overlay-k">
            <Dialog>
              <DialogTrigger asChild>
                <Button variant="outline">Dialógus megnyitása</Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Ügyfél törlése</DialogTitle>
                </DialogHeader>
                <DialogBody>
                  <DialogDescription>
                    Biztosan törlöd? A művelet nem vonható vissza.
                  </DialogDescription>
                </DialogBody>
                <DialogFooter>
                  <DialogClose asChild>
                    <Button variant="secondary">Mégse</Button>
                  </DialogClose>
                  <Button variant="destructive">Törlés</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline">Menü</Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent>
                <DropdownMenuLabel>Műveletek</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem>Szerkesztés</DropdownMenuItem>
                <DropdownMenuItem>Duplikálás</DropdownMenuItem>
                <DropdownMenuItem className="text-destructive">Törlés</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            <Tooltip>
              <TooltipTrigger asChild>
                <Button variant="ghost">Vidd fölé</Button>
              </TooltipTrigger>
              <TooltipContent>Tooltip-tartalom</TooltipContent>
            </Tooltip>
          </Section>

          <Section title="Megerősítő modálok — típusok (default · figyelmeztető · veszélyes)">
            <Button variant="outline" onClick={() => setConfirmTone('default')}>
              Default megerősítő
            </Button>
            <Button variant="warning" onClick={() => setConfirmTone('warning')}>
              Figyelmeztető (warning)
            </Button>
            <Button variant="destructive" onClick={() => setConfirmTone('danger')}>
              Veszélyes (danger)
            </Button>
            <ConfirmDialog
              open={confirmTone !== null}
              onOpenChange={(o) => !o && setConfirmTone(null)}
              tone={confirmTone ?? 'default'}
              title={
                confirmTone === 'danger'
                  ? 'Ügyfél törlése'
                  : confirmTone === 'warning'
                    ? 'Figyelmeztetés'
                    : 'Megerősítés'
              }
              description={
                confirmTone === 'danger'
                  ? 'Biztosan törlöd? A művelet nem vonható vissza, és minden kapcsolódó adat elvész.'
                  : confirmTone === 'warning'
                    ? 'Ez a művelet hatással lehet más rekordokra. Biztosan folytatod?'
                    : 'Megerősíted a műveletet?'
              }
              confirmLabel={
                confirmTone === 'danger'
                  ? 'Törlés'
                  : confirmTone === 'warning'
                    ? 'Folytatás'
                    : 'Rendben'
              }
              cancelLabel="Mégse"
              onConfirm={() => setConfirmTone(null)}
            />
          </Section>

          <Section title="Tabs">
            <Tabs defaultValue="altalanos" className="w-full">
              <TabsList>
                <TabsTrigger value="altalanos">
                  <Squares2X2Icon />
                  Általános
                </TabsTrigger>
                <TabsTrigger value="szamlazas">
                  <ReceiptPercentIcon />
                  Számlázás
                </TabsTrigger>
                <TabsTrigger value="kontaktok">
                  <IdentificationIcon />
                  Kontaktok
                </TabsTrigger>
              </TabsList>
              <TabsContent value="altalanos" className="text-muted-foreground text-sm">
                Általános adatok tartalma.
              </TabsContent>
              <TabsContent value="szamlazas" className="text-muted-foreground text-sm">
                Számlázási adatok tartalma.
              </TabsContent>
              <TabsContent value="kontaktok" className="text-muted-foreground text-sm">
                Kontaktok tartalma.
              </TabsContent>
            </Tabs>
          </Section>

          <Section title="Betöltés + üres állapot">
            <div className="w-full space-y-2">
              <Skeleton className="h-4 w-2/3" />
              <Skeleton className="h-4 w-1/2" />
              <Skeleton className="h-4 w-1/3" />
            </div>
            <div className="w-full">
              <EmptyState
                title="Nincs találat"
                description="Próbálj más szűrőt, vagy hozz létre új elemet."
              />
            </div>
          </Section>
        </div>
      </PageBody>
    </AppShell>
  );
}
